import TextClipper from '@/Components/TextClipper/TextClipper';

import {
  FILE_STATUS_ERROR,
  FILE_TYPE_EXPORTER,
  FILE_TYPE_EXTERNAL,
  FILE_TYPE_LINKED_SHEET,
} from '@/Utils/fileConstants';
import { isSharedByMe, isWorldReadable } from '@/Utils/sharing';

import { functionsSVG } from '@/Assets/icons';
import Icon from '@/Components/UI/Icon/Icon';
import Tooltip from '@/Components/UI/Tooltip/Tooltip';
import { checkCanAnalyzeJson, checkIsJSONSheet, checkIsMRFSheet } from '@/Utils/utils';

const FileNameCell = ({
  isFolder,
  row,
  fileSize,
  currentFile,
  isSharedParentFolder,
  liveQueryNotEnabled,
  user,
}) => {
  const fileName = row?.metadata?.FileName;

  const classNameCell = `file-title-cell ${isFolder ? 'folder-type' : 'file-type'}`;

  const getFileTypeClassName = () => {
    if (liveQueryNotEnabled) {
      return 'file-title-cell__external_not-enabled';
    }
    switch (row?.metadata?.Type) {
      case FILE_TYPE_EXPORTER:
        return 'file-title-cell__exporter';
      case FILE_TYPE_EXTERNAL:
        return 'file-title-cell__external';
      case FILE_TYPE_LINKED_SHEET:
        return 'file-title-cell__linked_sheet';
      default:
        return 'file-title-cell__omnivore';
    }
  };

  const isJsonFilePreviewMode = () => {
    const isMRFSheetResult = checkIsMRFSheet(currentFile?.metadata?.ParserUsed);
    const isJSONSheetResult = checkIsJSONSheet(currentFile?.metadata?.ParserUsed);
    const canAnalyzeJson = checkCanAnalyzeJson(user?.userProperties);
    return (isMRFSheetResult || isJSONSheetResult) && !canAnalyzeJson;
  };

  const renderJsonPreviewTooltip = () => {
    if (!isJsonFilePreviewMode()) return null;
    return (
      <Tooltip
        text='Limited Access: JSON files in your library are available in preview mode. Upgrade to unlock full features.'
        side='bottom'
        className='w-80 !text-left uppercase'
      >
        <div className='inline-flex items-center h-6 p-1 ml-1 rounded-2xl bg-carrot-100 whitespace-nowrap text-carrot-900 w-fit'>
          <Icon name='warning-circle' size='16' />
        </div>
      </Tooltip>
    );
  };

  const renderThreatDetection = () => {
    const threat = row?.metadata?.ThreatDetectionStatus;
    if (threat?.detected_row_count > 0) {
      return (
        <div className='ico-indicator'>
          <img
            src={threat.status === 'applied' ? functionsSVG.IoCGrayIcon : functionsSVG.IoCIcon}
            alt=''
            className='ioc-icon'
          />
          <span>{threat.detected_row_count} rows with known IOCs</span>
        </div>
      );
    }
    return null;
  };

  const isPublic =
    isSharedByMe(currentFile) ||
    isWorldReadable(row) ||
    (isSharedParentFolder && !row.metadata.Type);

  const renderFolderTitle = () => {
    let folderClassName = 'file-title-cell__folder';
    let cypressFolderAccess = 'folder-private';
    if (isPublic) {
      folderClassName = 'file-title-cell__shared_folder';
      cypressFolderAccess = 'folder-shared';
    }
    if (currentFile?.metadata?.Type === FILE_TYPE_EXTERNAL) {
      folderClassName = !currentFile?.metadata?.ParentDirectory
        ? 'file-title-cell__database'
        : 'file-title-cell__warehouse';
    }

    return (
      <>
        <div data-cy={cypressFolderAccess} className={folderClassName} />
        <TextClipper text={fileName} id={fileName} className='pl-[33px]' />
      </>
    );
  };

  const renderFileTitle = () => {
    const isRootDirectory =
      row?.metadata?.ParentDirectory === '' || row?.metadata?.ParentDirectory === undefined;

    const iconClass =
      row?.metadata?.Status === FILE_STATUS_ERROR
        ? 'file-title-cell__error'
        : getFileTypeClassName();

    // TextClipper props vary slightly based on whether the file is in the root directory
    const textClipperProps = isRootDirectory
      ? {
          'data-cy-handle': row.metadata.Type === FILE_TYPE_EXPORTER ? row.metadata.FileUuid : ' ',
          'data-cy': `tr_${fileName}_${fileSize === 'Loading' ? 'processing' : 'processed'}`,
          className: 'pl-[33px]',
        }
      : { className: 'file-type__opened pl-[33px]' };

    return (
      <>
        <div className={iconClass} />
        <TextClipper text={fileName} {...textClipperProps} />
        {renderJsonPreviewTooltip()}
        {isRootDirectory && renderThreatDetection()}
      </>
    );
  };

  return (
    <div className={classNameCell}>
      <div className='file-title-cell__container'>
        {isFolder ? renderFolderTitle() : renderFileTitle()}
      </div>
    </div>
  );
};

export default FileNameCell;
